// eslint-disable-next-line import/named
import { EventContentArg } from '@fullcalendar/core';
import { BookingCompressed } from '@jooxter/api';
import { useBookingActions } from './useBookingActions';
import { useCallback } from 'react';
import { JxtEventColorsEnum } from '@jooxter/utils';
import { useFetchResourceTypes } from '../../queries';
import { getResourceType } from '../../utils';
import { useOffCanvasStore } from '../../store';

export const useBookingCallbacks = () => {
  const { checkin, checkout, arrive, leave, cancel, deleteBookingModal } = useBookingActions();
  const { resourceTypes } = useFetchResourceTypes();
  const { close, openBookingForEdit, openBookingView } = useOffCanvasStore((state) => ({
    close: state.close,
    openBookingForEdit: state.openBookingForEdit,
    openBookingView: state.openBookingView,
  }));

  const onClickCheckIn = useCallback(
    (id: string, arriveCapability: boolean) => {
      if (arriveCapability) {
        arrive(id);
      } else {
        checkin(id);
      }
    },
    [arrive, checkin]
  );

  const onClickCheckOut = useCallback(
    (id: string, leaveCapability: boolean) => {
      if (leaveCapability) {
        leave(id);
      } else {
        checkout(id);
      }
    },
    [leave, checkout]
  );

  const onClickUpdate = useCallback(
    (id: string): void => {
      openBookingForEdit(parseInt(id, 10));
    },
    [openBookingForEdit]
  );

  const onClickDelete = useCallback(
    (event: EventContentArg) => {
      const booking: BookingCompressed = event.event.extendedProps.originalBooking;
      cancel({
        id: booking.id,
        rrule: booking.rrule,
        summary: {
          title: booking.summary,
          color: JxtEventColorsEnum.parse(booking.color),
        },
        start: { dateTime: booking.start.dateTime },
        end: { dateTime: booking.end.dateTime },
        resourceName: booking.resource.name,
        resourceType: getResourceType(booking.resource.resourceTypeId, resourceTypes),
      });
      close();
    },
    [cancel, close, resourceTypes]
  );

  const onShowBookingDetailsClick = useCallback(
    (id: number): void => {
      openBookingView(id);
    },
    [openBookingView]
  );

  return {
    onClickCheckIn,
    onClickCheckOut,
    onClickUpdate,
    onClickDelete,
    onShowBookingDetailsClick,
    deleteBookingModal,
  };
};
